import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import { setIsLoading } from "../../slices/reportSlice";
import * as services from "../../services/reportApi";
import dayjs from "dayjs"

function useAnnualPND1Report() {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [showReport, setShowReport] = useState(false);
    const [positionOptions, setPositionOptions] = useState([]);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdf2Url, setPdf2Url] = useState(null);
    const [search, setSearch] = useState({});
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [searchValues, setSearchValues] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDownload, setIsDownload] = useState(false);
    const [mode, setMode] = useState("M01");

    const showModal = () => {
        setIsModalOpen(true);
    };

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, positionOptions]);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, departmentListOption]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
            // alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    useEffect(() => {
        if (currentCompany) {
            getPosition();
            getDepartment();
        }
    }, [currentCompany, getPosition, getDepartment])

    const previewAnnualPND1ReportPdf = useCallback(async (params = {}, changeYear) => {
        try {
            dispatch(setIsLoading(true));

            const response = await services.PreviewAnnualPND1Report({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...params,
                ...params.pagination,
                ...search,
                ...pagination,
                isReport: "Y",
                sortBy: params.sortBy,
                year: params.year,
                month: params?.month?.format?.("MM") || params?.month
            })

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            setPdfUrl(url);

        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch, pagination, search])

    const exportAnnualPND1ReportExcel = useCallback(
        async (params = {}) => {
            setIsDownload(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportAnnualPND1Report({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Annual PND1 report.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownload(false);
            }
        },
        [currentCompany, dispatch, pagination, search]
    );
    const previewAnnualPND3ReportPdf = useCallback(async (params = {}, changeYear) => {
        try {
            dispatch(setIsLoading(true));

            const response = await services.PreviewAnnualPND3Report({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...params,
                ...params.pagination,
                ...search,
                ...pagination,
                isReport: "Y",
                sortBy: params.sortBy,
                year: params.year,
                month: params?.month?.format?.("MM") || params?.month
            })

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            setPdf2Url(url);

        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch, pagination, search])

    const exportAnnualPND3ReportExcel = useCallback(
        async (params = {}) => {
            setIsDownload(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportAnnualPND3Report({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Annual PND1 report.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownload(false);
            }
        },
        [currentCompany, dispatch, pagination, search]
    );

    const handleSearch = (values) => {
        const selectedYear = values.year?.year() || dayjs().year();

        setShowReport(true);
        setSearchValues(values);
        setSearch({ year: selectedYear });
        if (mode == 'M01') {
            previewAnnualPND1ReportPdf({
                ...values,
                year: selectedYear,
                month: values.month
            });
        } else if (mode == 'M02') {
            previewAnnualPND3ReportPdf({
                ...values,
                year: selectedYear,
                month: values.month
            });
        }

    };

    return {
        isModalOpen,
        showReport,
        positionOptions: modifiedPositionOptions,
        departmentOptions: modifiedDepartmentOption,
        pdfUrl,
        pdf2Url,
        searchValues,
        handleSearch,
        exportAnnualPND1ReportExcel,
        previewAnnualPND3ReportPdf,
        exportAnnualPND3ReportExcel,
        setIsModalOpen,
        showModal,
        isDownload,
        setMode,
        mode
    }
}

export default useAnnualPND1Report;